import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	useTranslationAuth,
	useTranslationCommonUi,
	useTranslationUserProfiles,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Button, Flex } from '@mantine/core';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';

interface UserFormData {
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
}

interface Props {
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	showEditButton?: boolean;
	onUpdate: (data: UserFormData) => Promise<void>;
}

export const UserDetails = ({
	firstName,
	lastName,
	phoneNumber,
	email,
	showEditButton,
	onUpdate,
}: Props) => {
	const [isEditing, setIsEditing] = useState(false);
	const tCommonUi = useTranslationCommonUi();
	const tUserProfiles = useTranslationUserProfiles();
	const tAuth = useTranslationAuth();

	const {
		control,
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitting },
	} = useForm<UserFormData>({
		mode: 'all',
		reValidateMode: 'onChange',
		defaultValues: {
			firstName,
			lastName,
			phoneNumber,
			email,
		},
	});

	const handleCancel = () => {
		reset();
		setIsEditing(false);
	};

	const onSubmit = async (data: UserFormData) => {
		await onUpdate(data);
		setIsEditing(false);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Flex direction="column" h="100%" w="100%">
				<ContainerWithHeader
					title={tUserProfiles('personal_details.title')}
					icon={<Icon variant="user" size="xl" />}
					headerActions={
						isEditing ? (
							<Flex gap="0.5rem">
								<Button
									variant="subtle"
									onClick={handleCancel}
									disabled={isSubmitting}
								>
									<Flex gap="0.5rem" align="center">
										<Icon variant="close" size="lg" />
										{tCommonUi('common_buttons.cancel')}
									</Flex>
								</Button>
								<Button
									type="submit"
									disabled={!isDirty || isSubmitting}
									loading={isSubmitting}
								>
									<Flex gap="0.5rem" align="center">
										<Icon variant="save" size="lg" />
										{tCommonUi('common_buttons.save')}
									</Flex>
								</Button>
							</Flex>
						) : (
							showEditButton && (
								<Button onClick={() => setIsEditing(true)}>
									<Flex gap="0.5rem" align="center">
										<Icon variant="pencil-edit-line" size="lg" />
										{tCommonUi(`common_buttons.edit`)}
									</Flex>
								</Button>
							)
						)
					}
				>
					<Flex direction="column" gap="1rem" w="100%">
						<Flex
							direction="row"
							gap="1rem"
							className="fields-wrapper"
							data-testid="to be added"
						>
							<TextInput
								data-autofocus
								name="firstName"
								control={control}
								size="md"
								disabled={!isEditing}
								placeholder={tAuth('register.first_name.label')}
								label={tAuth('register.first_name.label')}
								w="50%"
							/>
							<TextInput
								data-autofocus
								name="lastName"
								control={control}
								size="md"
								disabled={!isEditing}
								placeholder={tAuth('register.last_name.label')}
								label={tAuth('register.last_name.label')}
								w="50%"
							/>
						</Flex>
						<Flex
							direction="row"
							gap="1rem"
							className="fields-wrapper"
							data-testid="to be added"
						>
							<TextInput
								data-autofocus
								name="phoneNumber"
								control={control}
								type="tel"
								size="md"
								disabled={!isEditing}
								placeholder={tUserProfiles('personal_details.phone_number')}
								label={tUserProfiles('personal_details.phone_number')}
								w="50%"
							/>
							<TextInput
								data-autofocus
								name="email"
								control={control}
								size="md"
								type="email"
								disabled
								placeholder={tCommonUi('investors.email')}
								label={tCommonUi('investors.email')}
								w="50%"
							/>
						</Flex>
					</Flex>
				</ContainerWithHeader>
			</Flex>
		</form>
	);
};
